import React from 'react';
import PropTypes from 'prop-types';
import { graphql, gql, compose } from 'react-apollo';

// Style
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import s from './CancellationPolicies.css';

// Component
import CancellationPolicyForm from '../../../components/siteadmin/CancellationPolicyForm';
import CancellationPolicySharedForm from '../../../components/siteadmin/CancellationPolicySharedForm';
import CancellationPolicySettings from '../../../components/siteadmin/CancellationPolicySettings';
import Loader from '../../../components/Loader';

// GraphQL query for fetching policy from DynamicContent
const GET_CANCELLATION_POLICY_DYNAMIC = gql`
  query getCancellationPolicyDynamic($policyType: String!) {
    getCancellationPolicyDynamic(policyType: $policyType) {
      status
      policy {
        policyType
        contentOne { es en fr it pt }
        contentTwo { es en fr it pt }
        beforeDaysTitle { es en fr it pt }
        beforeDaysContentOne { es en fr it pt }
        beforeDaysContentTwo { es en fr it pt }
        checkInTitle { es en fr it pt }
        checkInContentOne { es en fr it pt }
        checkInContentTwo { es en fr it pt }
        checkOutTitle { es en fr it pt }
        checkOutContentOne { es en fr it pt }
        checkOutContentTwo { es en fr it pt }
      }
      shared {
        allowOwner { es en fr it pt }
        checkInTitle { es en fr it pt }
        checkOutTitle { es en fr it pt }
        description { es en fr it pt }
        desc1 { es en fr it pt }
        desc2 { es en fr it pt }
        desc3 { es en fr it pt }
        tripStart { es en fr it pt }
        desc4 { es en fr it pt }
        desc5 { es en fr it pt }
        desc6 { es en fr it pt }
        review { es en fr it pt }
        desc7 { es en fr it pt }
      }
    }
  }
`;

// Helper to transform DynamicContent format to legacy form format
const transformPolicyToLegacy = (policy) => {
  if (!policy) return {};
  const result = { policyType: policy.policyType };
  const fields = ['contentOne', 'contentTwo', 'beforeDaysTitle', 'beforeDaysContentOne',
    'beforeDaysContentTwo', 'checkInTitle', 'checkInContentOne', 'checkInContentTwo',
    'checkOutTitle', 'checkOutContentOne', 'checkOutContentTwo'];

  fields.forEach(field => {
    if (policy[field]) {
      result[field] = policy[field].es || '';
      result[`${field}_en`] = policy[field].en || '';
      result[`${field}_fr`] = policy[field].fr || '';
      result[`${field}_it`] = policy[field].it || '';
      result[`${field}_pt`] = policy[field].pt || '';
    }
  });
  return result;
};

const transformSharedToLegacy = (shared) => {
  if (!shared) return {};
  const result = {};
  const fieldMap = {
    allowOwner: 'allowOwner',
    checkInTitle: 'cancellationCheckIn',
    checkOutTitle: 'cancellationCheckOut',
    description: 'cancellationDescription',
    desc1: 'cancellationDesc1',
    desc2: 'cancellationDesc2',
    desc3: 'cancellationDesc3',
    tripStart: 'cancellationTripStart',
    desc4: 'cancellationDesc4',
    desc5: 'cancellationDesc5',
    desc6: 'cancellationDesc6',
    review: 'cancellationReview',
    desc7: 'cancellationDesc7',
  };

  Object.keys(fieldMap).forEach(key => {
    const legacyKey = fieldMap[key];
    if (shared[key]) {
      result[legacyKey] = shared[key].es || '';
      result[`${legacyKey}_en`] = shared[key].en || '';
      result[`${legacyKey}_fr`] = shared[key].fr || '';
      result[`${legacyKey}_it`] = shared[key].it || '';
      result[`${legacyKey}_pt`] = shared[key].pt || '';
    }
  });
  return result;
};

class CancellationPolicies extends React.Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      policyType: 'Settings' // Default to Settings tab
    };
  }

  handlePolicyChange = async (policyType) => {
    this.setState({ policyType });
    // Refetch data for the new policy type if not Settings
    if (policyType !== 'Settings') {
      await this.props.dynamicData.refetch({ policyType });
    }
  };

  render() {
    const { dynamicData, title } = this.props;
    const { policyType } = this.state;

    // If Settings tab is selected, show CancellationPolicySettings
    if (policyType === 'Settings') {
      return (
        <CancellationPolicySettings
          title={title}
          policyType={policyType}
          onPolicyChange={this.handlePolicyChange}
          showTabs={true}
        />
      );
    }

    if (dynamicData?.loading) {
      return <Loader type={"text"} />;
    }

    const dynamicResult = dynamicData?.getCancellationPolicyDynamic;

    // If Shared tab is selected, show SharedForm
    if (policyType === 'Shared') {
      const sharedInitialValues = transformSharedToLegacy(dynamicResult?.shared);
      return (
        <CancellationPolicySharedForm
          initialValues={sharedInitialValues}
          title={title}
          policyType={policyType}
          onPolicyChange={this.handlePolicyChange}
          showTabs={true}
        />
      );
    }

    // Transform policy data to legacy format for form
    const initialValues = {
      ...transformPolicyToLegacy(dynamicResult?.policy),
      policyType
    };

    return (
      <CancellationPolicyForm
        initialValues={initialValues}
        title={title}
        policyType={policyType}
        onPolicyChange={this.handlePolicyChange}
      />
    );
  }
}

export default compose(
  withStyles(s),
  graphql(GET_CANCELLATION_POLICY_DYNAMIC, {
    name: 'dynamicData',
    options: (props) => ({
      variables: {
        policyType: 'Flexible'
      },
      ssr: false,
      fetchPolicy: 'network-only'
    })
  }),
)(CancellationPolicies);

